import { indexOf } from "lodash-es";
import { clone } from "ramda";

import { formatNumberByThousands } from "widgets/utils";

import { type ISelectOption } from "@/app-utils/form.ts";
import { type BlastAdvancedOptions, type BlastJob, type BlastParams } from "@/graphql.ts";
import { type IBlastPrefilledValuesState } from "@/redux/modules/blast.ts";
import { BLAST_METHOD } from "@/settings/settings";

/*
 * Constants and helper functions for BLAST input page.
 */

interface IDependingSelectOption extends ISelectOption {
  depends: string[];
}

const FORM_KEYS = {
  BLAST_INPUT_FORM_BLAST_METHOD: "blastMethod",
  BLAST_INPUT_FORM_SEQUENCE_FIELD: "sequence",
  BLAST_INPUT_FORM_BLAST_INDEX_FIELD: "blastIndex",
  BLAST_INPUT_FORM_EVENT_NAME_FIELD: "eventName",
  BLAST_INPUT_FORM_EVENT_DESCRIPTION_FIELD: "eventDescription",
  ADVANCED: {
    BLAST_INPUT_FORM_MAX_TARGET_SEQ_FIELD: "maxTargetSeq",
    BLAST_INPUT_FORM_SHORT_QUERY_FIELD: "shortQueries",
    BLAST_INPUT_FORM_EXPECT_THRESHOLD_FIELD: "expectThreshold",
    BLAST_INPUT_FORM_WORD_SIZE_FIELD: "wordSize",
    BLAST_INPUT_FORM_MAX_MATCHES_FIELD: "maxMatches",
    BLAST_INPUT_FORM_MATCH_MISMATCH_SCORE_FIELD: "matchMismatchScores",
    BLAST_INPUT_FORM_GAP_COST_FIELD: "gapCost",
    BLAST_INPUT_FORM_FILTER_LOW_COMPLEXITY_FIELD: "filterLowComplexity",
    BLAST_INPUT_FORM_MASK_TABLE_FIELD: "maskTable",
    BLAST_INPUT_FORM_MASK_LOWER_FIELD: "maskLower",
    BLAST_INPUT_FORM_MATRIX_FIELD: "matrix",
    BLAST_INPUT_FORM_COMPOSITIONAL_ADJUSTMENTS_FIELD: "compositionalAdjustments",
    BLAST_INPUT_FORM_GENETIC_CODE_FIELD: "genCode",
  },
};

const maxTargetSeqOptions: ISelectOption[] = [
  { value: 10, label: "10" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 250, label: "250" },
  { value: 500, label: "500" },
  { value: 1000, label: formatNumberByThousands(1000) },
  { value: 5000, label: formatNumberByThousands(5000) },
  { value: 9999, label: formatNumberByThousands(9999) },
];

const matrixOptions: ISelectOption[] = [
  { value: "PAM30", label: "PAM30" },
  { value: "PAM70", label: "PAM70" },
  { value: "PAM250", label: "PAM250" },
  { value: "BLOSUM80", label: "BLOSUM80" },
  { value: "BLOSUM62", label: "BLOSUM62" },
  { value: "BLOSUM45", label: "BLOSUM45" },
  { value: "BLOSUM50", label: "BLOSUM50" },
  { value: "BLOSUM90", label: "BLOSUM90" },
];

const wordSizeOptions: IDependingSelectOption[] = [
  { value: 2, label: "2", depends: [BLAST_METHOD.BLASTP] },
  { value: 3, label: "3", depends: [BLAST_METHOD.BLASTP] },
  { value: 6, label: "6", depends: [BLAST_METHOD.BLASTP] },
  { value: 7, label: "7", depends: [BLAST_METHOD.BLASTN] },
  { value: 11, label: "11", depends: [BLAST_METHOD.BLASTN] },
  { value: 15, label: "15", depends: [BLAST_METHOD.BLASTN] },
];

const proteinWordSizeDefault: ISelectOption = wordSizeOptions[2];
const nucleotideWordSizeDefault: ISelectOption = wordSizeOptions[4];

const fourMinusFiveMatchMismatchScoreOption = "4,-5";

const matchMismatchScoreOptions: ISelectOption[] = [
  { value: "1,-2", label: "1,-2" },
  { value: "1,-3", label: "1,-3" },
  { value: "1,-4", label: "1,-4" },
  { value: "2,-3", label: "2,-3" },
  { value: fourMinusFiveMatchMismatchScoreOption, label: fourMinusFiveMatchMismatchScoreOption },
  { value: "1,-1", label: "1,-1" },
];

const gapCostOptionsN: IDependingSelectOption[] = [
  {
    value: "Existence: 12 Extension: 8",
    label: "Existence: 12 Extension: 8",
    depends: [fourMinusFiveMatchMismatchScoreOption],
  },

  {
    value: "Existence: 6 Extension: 5",
    label: "Existence: 6 Extension: 5",
    depends: [fourMinusFiveMatchMismatchScoreOption],
  },
  {
    value: "Existence: 5 Extension: 5",
    label: "Existence: 5 Extension: 5",
    depends: [fourMinusFiveMatchMismatchScoreOption],
  },
  {
    value: "Existence: 4 Extension: 5",
    label: "Existence: 4 Extension: 5",
    depends: [fourMinusFiveMatchMismatchScoreOption],
  },
  {
    value: "Existence: 3 Extension: 5",
    label: "Existence: 3 Extension: 5",
    depends: [fourMinusFiveMatchMismatchScoreOption],
  },

  {
    value: "Existence: 4 Extension: 4",
    label: "Existence: 4 Extension: 4",
    depends: ["2,-3"],
  },
  {
    value: "Existence: 2 Extension: 4",
    label: "Existence: 2 Extension: 4",
    depends: ["2,-3"],
  },
  {
    value: "Existence: 0 Extension: 4",
    label: "Existence: 0 Extension: 4",
    depends: ["2,-3"],
  },

  {
    value: "Existence: 3 Extension: 3",
    label: "Existence: 3 Extension: 3",
    depends: ["2,-3"],
  },

  {
    value: "Existence: 6 Extension: 2",
    label: "Existence: 6 Extension: 2",
    depends: ["2,-3"],
  },
  {
    value: "Existence: 5 Extension: 2",
    label: "Existence: 5 Extension: 2",
    depends: ["1,-2", "1,-3", "2,-3", "1,-1", "1,-4"],
  },
  {
    value: "Existence: 4 Extension: 2",
    label: "Existence: 4 Extension: 2",
    depends: ["2,-3"],
  },
  {
    value: "Existence: 3 Extension: 2",
    label: "Existence: 3 Extension: 2",
    depends: ["1,-1"],
  },
  {
    value: "Existence: 2 Extension: 2",
    label: "Existence: 2 Extension: 2",
    depends: ["1,-2", "1,-3", "2,-3", "1,-1"],
  },
  {
    value: "Existence: 1 Extension: 2",
    label: "Existence: 1 Extension: 2",
    depends: ["1,-2", "1,-3", "1,-4", "1,-1"],
  },
  {
    value: "Existence: 0 Extension: 2",
    label: "Existence: 0 Extension: 2",
    depends: ["1,-2", "1,-3", "1,-4", "1,-1"],
  },

  {
    value: "Existence: 4 Extension: 1",
    label: "Existence: 4 Extension: 1",
    depends: ["1,-1"],
  },
  {
    value: "Existence: 3 Extension: 1",
    label: "Existence: 3 Extension: 1",
    depends: ["1,-2", "1,-1"],
  },
  {
    value: "Existence: 2 Extension: 1",
    label: "Existence: 2 Extension: 1",
    depends: ["1,-2", "1,-3", "1,-4", "1,-1"],
  },
  {
    value: "Existence: 1 Extension: 1",
    label: "Existence: 1 Extension: 1",
    depends: ["1,-2", "1,-3", "1,-4"],
  },
];

const fourMinusFiveGapCostDefault: ISelectOption = gapCostOptionsN[0];

const nucleotideGapCostDefault: ISelectOption = gapCostOptionsN[10];

const gapCostOptionsP: IDependingSelectOption[] = [
  {
    value: "Existence: 15 Extension: 3",
    label: "Existence: 15 Extension: 3",
    depends: ["PAM30", "PAM250"],
  },
  {
    value: "Existence: 14 Extension: 3",
    label: "Existence: 14 Extension: 3",
    depends: ["PAM250"],
  },
  {
    value: "Existence: 13 Extension: 3",
    label: "Existence: 13 Extension: 3",
    depends: ["BLOSUM45", "BLOSUM50", "PAM30", "PAM250"],
  },
  {
    value: "Existence: 12 Extension: 3",
    label: "Existence: 12 Extension: 3",
    depends: ["BLOSUM45", "BLOSUM50", "PAM70", "PAM250"],
  },
  {
    value: "Existence: 11 Extension: 3",
    label: "Existence: 11 Extension: 3",
    depends: ["BLOSUM45", "BLOSUM50", "PAM250"],
  },
  {
    value: "Existence: 10 Extension: 3",
    label: "Existence: 10 Extension: 3",
    depends: ["BLOSUM45", "BLOSUM50"],
  },
  {
    value: "Existence: 9 Extension: 3",
    label: "Existence: 9 Extension: 3",
    depends: ["BLOSUM50"],
  },

  {
    value: "Existence: 17 Extension: 2",
    label: "Existence: 17 Extension: 2",
    depends: ["PAM250"],
  },
  {
    value: "Existence: 16 Extension: 2",
    label: "Existence: 16 Extension: 2",
    depends: ["BLOSUM50", "PAM250"],
  },
  {
    value: "Existence: 15 Extension: 2",
    label: "Existence: 15 Extension: 2",
    depends: ["BLOSUM45", "BLOSUM50", "PAM250"],
  },
  {
    value: "Existence: 14 Extension: 2",
    label: "Existence: 14 Extension: 2",
    depends: ["BLOSUM45", "BLOSUM50", "PAM30", "PAM250"],
  },
  {
    value: "Existence: 13 Extension: 2",
    label: "Existence: 13 Extension: 2",
    depends: ["BLOSUM45", "BLOSUM50", "PAM250"],
  },
  {
    value: "Existence: 12 Extension: 2",
    label: "Existence: 12 Extension: 2",
    depends: ["BLOSUM45", "BLOSUM50"],
  },
  {
    value: "Existence: 11 Extension: 2",
    label: "Existence: 11 Extension: 2",
    depends: ["BLOSUM62", "PAM70"],
  },
  {
    value: "Existence: 10 Extension: 2",
    label: "Existence: 10 Extension: 2",
    depends: ["BLOSUM62"],
  },
  {
    value: "Existence: 9 Extension: 2",
    label: "Existence: 9 Extension: 2",
    depends: ["BLOSUM62", "BLOSUM90"],
  },
  {
    value: "Existence: 8 Extension: 2",
    label: "Existence: 8 Extension: 2",
    depends: ["BLOSUM62", "BLOSUM80", "BLOSUM90", "PAM70"],
  },
  {
    value: "Existence: 7 Extension: 2",
    label: "Existence: 7 Extension: 2",
    depends: ["BLOSUM62", "BLOSUM80", "BLOSUM90", "PAM30", "PAM70"],
  },
  {
    value: "Existence: 6 Extension: 2",
    label: "Existence: 6 Extension: 2",
    depends: ["BLOSUM62", "BLOSUM80", "BLOSUM90", "PAM30", "PAM70"],
  },
  {
    value: "Existence: 5 Extension: 2",
    label: "Existence: 5 Extension: 2",
    depends: ["PAM30"],
  },

  {
    value: "Existence: 21 Extension: 1",
    label: "Existence: 21 Extension: 1",
    depends: ["PAM250"],
  },
  {
    value: "Existence: 20 Extension: 1",
    label: "Existence: 20 Extension: 1",
    depends: ["PAM250"],
  },
  {
    value: "Existence: 19 Extension: 1",
    label: "Existence: 19 Extension: 1",
    depends: ["BLOSUM45", "BLOSUM50", "PAM250"],
  },
  {
    value: "Existence: 18 Extension: 1",
    label: "Existence: 18 Extension: 1",
    depends: ["BLOSUM45", "BLOSUM50", "PAM250"],
  },
  {
    value: "Existence: 17 Extension: 1",
    label: "Existence: 17 Extension: 1",
    depends: ["BLOSUM45", "BLOSUM50", "PAM250"],
  },
  {
    value: "Existence: 16 Extension: 1",
    label: "Existence: 16 Extension: 1",
    depends: ["BLOSUM45", "BLOSUM50"],
  },
  {
    value: "Existence: 15 Extension: 1",
    label: "Existence: 15 Extension: 1",
    depends: ["BLOSUM50"],
  },
  {
    value: "Existence: 14 Extension: 1",
    label: "Existence: 14 Extension: 1",
    depends: ["PAM30"],
  },
  {
    value: "Existence: 13 Extension: 1",
    label: "Existence: 13 Extension: 1",
    depends: ["BLOSUM62"],
  },
  {
    value: "Existence: 12 Extension: 1",
    label: "Existence: 12 Extension: 1",
    depends: ["BLOSUM62"],
  },
  {
    value: "Existence: 11 Extension: 1",
    label: "Existence: 11 Extension: 1",
    depends: ["BLOSUM62", "BLOSUM80", "BLOSUM90", "PAM70"],
  },
  {
    value: "Existence: 10 Extension: 1",
    label: "Existence: 10 Extension: 1",
    depends: ["BLOSUM62", "BLOSUM80", "BLOSUM90", "PAM30", "PAM70"],
  },
  {
    value: "Existence: 9 Extension: 1",
    label: "Existence: 9 Extension: 1",
    depends: ["BLOSUM62", "BLOSUM80", "BLOSUM90", "PAM30", "PAM70"],
  },
  {
    value: "Existence: 8 Extension: 1",
    label: "Existence: 8 Extension: 1",
    depends: ["PAM30"],
  },
];

const proteinGapCostDefaults: any = {
  PAM30: gapCostOptionsP[32],
  PAM70: gapCostOptionsP[31],
  PAM250: gapCostOptionsP[10],
  BLOSUM80: gapCostOptionsP[31],
  BLOSUM62: gapCostOptionsP[30],
  BLOSUM45: gapCostOptionsP[9],
  BLOSUM50: gapCostOptionsP[11],
  BLOSUM90: gapCostOptionsP[31],
};

const proteinGapCostDefault: ISelectOption = gapCostOptionsP[31];

const compositionalAdjustmentOptions: ISelectOption[] = [
  { value: "No adjustment", label: "No adjustment" },
  { value: "Composition-based statistics", label: "Composition-based statistics" },
  {
    value: "Conditional compositional score matrix adjustment",
    label: "Conditional compositional score matrix adjustment",
  },
  {
    value: "Universal compositional score matrix adjustment",
    label: "Universal compositional score matrix adjustment",
  },
];

const genCodeTableOptions: ISelectOption[] = [
  { value: 1, label: "Standard (1)" },
  { value: 2, label: "Vertebrate Mitochondrial (2)" },
  { value: 3, label: "Yeast Mitochondrial (3)" },
  { value: 4, label: "Mold Mitochondrial; ... (4)" },
  { value: 5, label: "Invertebrate Mitochondrial (5)" },
  { value: 6, label: "Ciliate Nuclear; ... (6)" },
  { value: 9, label: "Echinoderm Mitochondrial (9)" },
  { value: 10, label: "Euplotid Nuclear (10)" },
  { value: 11, label: "Bacteria and Archaea (11)" },
  { value: 12, label: "Alternative Yeast Nuclear (12)" },
  { value: 13, label: "Ascidian Mitochondrial (13)" },
  { value: 14, label: "Flatworm Mitochondrial (14)" },
  { value: 16, label: "Chlorophycean Mitochondrial (16)" },
  { value: 21, label: "Trematode Mitochondrial (21)" },
  { value: 22, label: "Scenedesmus obliquus Mitochondrial (22)" },
  { value: 23, label: "Thraustochytrium Mitochondrial (23)" },
  { value: 24, label: "Pterobranchia Mitochondrial (24)" },
  { value: 25, label: "Candidate Division SR1 and Gracilibacteria (25)" },
  { value: 26, label: "Pachysolen tannophilus Nuclear (26)" },
  { value: 27, label: "Karyorelict Nuclear (27)" },
  { value: 28, label: "Condylostoma Nuclear (28)" },
  { value: 29, label: "Mesodinium Nuclear (29)" },
  { value: 30, label: "Peritrich Nuclear (30)" },
  { value: 31, label: "Blastocrithidia Nuclear (31)" },
];

const genCodeTableOptionsDefault: ISelectOption = genCodeTableOptions[0];

const getGapCostOptions = (blastMethod: string, currentDetermValue: ISelectOption): ISelectOption[] => {
  let gapCostOptions: IDependingSelectOption[];
  if (
    blastMethod === BLAST_METHOD.BLASTP ||
    blastMethod === BLAST_METHOD.BLASTX ||
    blastMethod === BLAST_METHOD.TBLASTN
  ) {
    gapCostOptions = gapCostOptionsP;
  } else {
    gapCostOptions = gapCostOptionsN;
  }

  return gapCostOptions.filter((o) => indexOf(o.depends, currentDetermValue?.value) > -1);
};

const getWordSizeOptions = (blastMethod: string): ISelectOption[] => {
  let searchType = "";
  switch (blastMethod) {
    case BLAST_METHOD.BLASTX:
    case BLAST_METHOD.BLASTP:
    case BLAST_METHOD.TBLASTN:
      searchType = BLAST_METHOD.BLASTP;
      break;
    case BLAST_METHOD.BLASTN:
      searchType = BLAST_METHOD.BLASTN;
      break;
    default:
      throw new Error("Unknown BLAST method encountered: " + blastMethod);
  }

  const filteredWordSizeOptions: any = wordSizeOptions.filter((o) => {
    return indexOf(o.depends, searchType) > -1;
  });

  return filteredWordSizeOptions as ISelectOption[];
};

export interface IBlastSelectOption extends ISelectOption {
  description: string;
}

export const BLAST_OPTIONS: IBlastSelectOption[] = [
  {
    value: BLAST_METHOD.BLASTN,
    label: BLAST_METHOD.BLASTN.toUpperCase(),
    description: "search nucleotide databases using a nucleotide query",
  },
  {
    value: BLAST_METHOD.BLASTP,
    label: BLAST_METHOD.BLASTP.toUpperCase(),
    description: "search protein databases using a protein query",
  },
  {
    value: BLAST_METHOD.TBLASTN,
    label: BLAST_METHOD.TBLASTN.toUpperCase(),
    description: "search translated nucleotide databases using a protein query",
  },
  {
    value: BLAST_METHOD.BLASTX,
    label: BLAST_METHOD.BLASTX.toUpperCase(),
    description: "search protein databases using a translated nucleotide query",
  },
];

const initialParams: any = {
  [FORM_KEYS.BLAST_INPUT_FORM_BLAST_METHOD]: BLAST_OPTIONS[0],
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_TARGET_SEQ_FIELD]: maxTargetSeqOptions[2],
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_SHORT_QUERY_FIELD]: true,
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_EXPECT_THRESHOLD_FIELD]: 10,
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_WORD_SIZE_FIELD]: wordSizeOptions[4],
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_MATCHES_FIELD]: 100,
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATCH_MISMATCH_SCORE_FIELD]: matchMismatchScoreOptions[0],
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GAP_COST_FIELD]: gapCostOptionsN[11],
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_FILTER_LOW_COMPLEXITY_FIELD]: true,
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MASK_TABLE_FIELD]: true,
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATRIX_FIELD]: matrixOptions[4],
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_COMPOSITIONAL_ADJUSTMENTS_FIELD]: compositionalAdjustmentOptions[2],
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MASK_LOWER_FIELD]: false,
  [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GENETIC_CODE_FIELD]: genCodeTableOptions[0],
};

function getInitialBLASTParams(initialJob: BlastJob, prefilledValues: IBlastPrefilledValuesState) {
  if (initialJob) {
    // Use BLAST parameters from existing job
    const p: BlastParams = initialJob.params.blast;
    const ao: BlastAdvancedOptions = initialJob.params.blast.advancedOptions;

    return {
      [FORM_KEYS.BLAST_INPUT_FORM_BLAST_METHOD]: BLAST_OPTIONS.find(
        (option: ISelectOption) => option.value === p.blastType,
      ),
      [FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD]: p.sequenceQuery,
      [FORM_KEYS.BLAST_INPUT_FORM_BLAST_INDEX_FIELD]: p.targets,
      [FORM_KEYS.BLAST_INPUT_FORM_EVENT_NAME_FIELD]: undefined,
      [FORM_KEYS.BLAST_INPUT_FORM_EVENT_DESCRIPTION_FIELD]: undefined,
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_TARGET_SEQ_FIELD]:
        ao.maxTargetSeqs &&
        ParamValues.maxTargetSeqOptions.find((option: ISelectOption) => option.value === ao.maxTargetSeqs),
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_SHORT_QUERY_FIELD]: ao.shortQueries,
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_EXPECT_THRESHOLD_FIELD]: ao.expectThreshold,
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_WORD_SIZE_FIELD]:
        ao.wordSize &&
        ParamValues.getWordSizeOptions(p.blastType).find((option: ISelectOption) => option.value === ao.wordSize),
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_MATCHES_FIELD]: ao.maxMatches,
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATCH_MISMATCH_SCORE_FIELD]:
        ao.matchMismatchScores &&
        ParamValues.matchMismatchScoreOptions.find((option: ISelectOption) => option.value === ao.matchMismatchScores),
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GAP_COST_FIELD]:
        ao.gapCost &&
        gapCostOptionsN.concat(gapCostOptionsP).find((option: ISelectOption) => option.value === ao.gapCost),
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_FILTER_LOW_COMPLEXITY_FIELD]: ao.filterLowComplexity,
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MASK_TABLE_FIELD]: ao.maskTable,
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATRIX_FIELD]:
        ao.matrix && ParamValues.matrixOptions.find((option: ISelectOption) => option.value === ao.matrix),
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_COMPOSITIONAL_ADJUSTMENTS_FIELD]:
        ao.compositionalAdjustments &&
        ParamValues.compositionalAdjustmentOptions.find(
          (option: ISelectOption) => option.value === ao.compositionalAdjustments,
        ),
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MASK_LOWER_FIELD]: ao.maskLower,
      [FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GENETIC_CODE_FIELD]:
        ao.genCode != null &&
        ParamValues.genCodeTableOptions.find((option: ISelectOption) => option.value === ao.genCode),
    };
  }

  // Use standard initial BLAST parameters as a basis
  const clonedParams = clone(initialParams);
  // ...and prefill the sequence field with identifiers send from table or genome browser
  if (prefilledValues) {
    if (prefilledValues.sequenceIdentifiers?.length > 0) {
      clonedParams[FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD] = prefilledValues.sequenceIdentifiers.join("\n");
    }
    if (prefilledValues.blastMethod) {
      clonedParams[FORM_KEYS.BLAST_INPUT_FORM_BLAST_METHOD] = BLAST_OPTIONS.find(
        (option: ISelectOption) => option.value === prefilledValues.blastMethod,
      );
    }
  }

  return clonedParams;
}

const ParamValues = {
  FORM_KEYS,
  getInitialBLASTParams,
  getGapCostOptions,
  getWordSizeOptions,
  compositionalAdjustmentOptions,
  proteinGapCostDefault,
  proteinGapCostDefaults,
  proteinWordSizeDefault,
  gapCostOptionsN,
  gapCostOptionsP,
  maxTargetSeqOptions,
  matchMismatchScoreOptions,
  matrixOptions,
  fourMinusFiveGapCostDefault,
  fourMinusFiveMatchMismatchScoreOption,
  nucleotideWordSizeDefault,
  nucleotideGapCostDefault,
  genCodeTableOptions,
  genCodeTableOptionsDefault,
};

export { ParamValues };
